import { Grid } from "@common/controls/Grid/component";
import { isGroupItem, isSearchMatch } from "@common/controls/Grid/lib";
import { type GenericMap, IconType } from "@common/declarations";
import { ContentAlign } from "@common/hocs/withDropdown/lib";
import { useI18n } from "@common/hooks/i18n";
import { useNavigation } from "@common/hooks/navigation";
import { downloadBlob } from "@common/lib/api";
import { exportPdf } from "@common/lib/pdf";
import classNames from "classnames";
import { identity, isFunction, isNull, isUndefined } from "lodash";
import * as React from "react";
import { flushSync } from "react-dom";
import { createRoot } from "react-dom/client";
import { type GridGroupInfo } from "../Grid/types";
import { IconSelectItem, SelectItem, type SelectItemProps, SelectStatic } from "../Inputs/Select/Base/component";
import GridViewBase from "./Base";
import ExportModal, { type ExportModalData } from "./ExportModal/component";
import { toCsv } from "./lib";
import {
  type Filter,
  type FilterValue,
  type GridContentProps,
  type GridViewActionCallbacks,
  type GridViewButton,
  type GridViewButtonComponent,
  type GridViewConfigProps,
  type GridViewItemMenuButton,
  type GridViewToolbar
} from "./types";

import "./style.less";

export type GridViewProps<T extends {}, D, ModalProps = void> = Omit<
  GridViewConfigProps<T, D, ModalProps>,
  "onItemMove"
> & {
  data: T[];
  filters?: Filter<T>[];
  isLoading: boolean;
  searchText?: string | null;
  children?: any;
};
const GridView = <T extends {}, D extends {}, ModalProps = void>({
  columns,
  exportOptions,
  onGroupClick,
  onRowClick,
  onRowMove,
  grouping,
  renderGroup,
  data,
  toolbar,
  tooltip,
  children,
  ...props
}: GridViewProps<T, D, ModalProps>) => {
  const i18n = useI18n();

  const [visibleModal, setVisibleModal] = React.useState<"export" | null>(null);
  const [filteredData, setFilteredData] = React.useState<T[]>([]);
  const navigation = useNavigation();

  const filterFnInternal = React.useCallback(
    (text: string | undefined | null, filterValues: GenericMap<FilterValue>) => {
      const filters = props.filters ?? [];
      return data.filter((x) => {
        if (grouping && isGroupItem(x)) return false;

        if (!!text && !columns.some((c) => isSearchMatch(i18n, c, x, text))) return false;

        return Object.keys(filterValues)
          .filter((y) => !isNull(filterValues[y]) && !isUndefined(filterValues[y]))
          .every((y) => {
            const filter = filters.find((x) => x.name === y)!;
            return (x[filter.itemProp] as any) === filterValues[y];
          });
      });
    },
    [i18n, data, columns, props.filters, grouping]
  );

  const filterFn = React.useCallback(
    (text: string, filterValues: GenericMap<FilterValue>) => {
      props.updateFilters && props.updateFilters(text, filterValues);

      if (props.filterFn) {
        return props.filterFn(text, filterValues);
      }

      if (filterValues) {
        Object.keys(filterValues).forEach((x) => navigation.upsertQs(x, filterValues[x]?.toString()));
      }

      return filterFnInternal(text, filterValues);
    },
    [props.filterFn, filterFnInternal, props.updateFilters]
  );

  React.useEffect(() => {
    // if filterFn is present, we assume that the data is already filtered
    if (props.filterFn) return setFilteredData(data);

    const filteredData = filterFnInternal(
      props.searchText,
      (props.filters ?? []).reduce((res, x) => ({ ...res, [x.name]: x.value }), {})
    );
    setFilteredData(filteredData);
  }, [props.filterFn, data, filterFnInternal, props.filters, props.searchText]);

  const _expandItemMenu = React.useCallback(
    (itemMenu: GridViewItemMenuButton<T>[], args: GridViewActionCallbacks<T>): GridViewButton<T>[] =>
      itemMenu
        .map((item) => {
          if (item === "edit") {
            return !!props.edit
              ? {
                  icon: IconType.Cog,
                  label: !isFunction(props.edit) && !!props.edit.label ? props.edit.label : i18n.update,
                  isVisible: !isFunction(props.edit) && props.edit.isVisible,
                  isDisabled: !isFunction(props.edit) && props.edit.isDisabled,
                  tooltipMessage: !isFunction(props.edit) && props.edit.tooltipMessage,
                  onClick: args.onEditClick
                }
              : null;
          }
          if (item === "remove") {
            return !!props.remove
              ? {
                  icon: IconType.Delete,
                  label: i18n.delete,
                  isVisible: !isFunction(props.remove) && props.remove.isVisible,
                  isDisabled: !isFunction(props.remove) && props.remove.isDisabled,
                  tooltipMessage: !isFunction(props.remove) && props.remove.tooltipMessage,
                  onClick: args.onDeleteClick
                }
              : null;
          }
          return item;
        })
        .filter((btn) => btn !== null) as GridViewButton<T>[],
    [props.edit, props.remove]
  );

  const _getMenu = React.useCallback(() => {
    const { itemMenu = ["edit", "remove"] } = props;
    return itemMenu && itemMenu.length ? itemMenu : null;
  }, [props.itemMenu]);

  const _renderRowMenu = React.useCallback(
    (args: GridContentProps<T, T>, item: T) => {
      const menu = _getMenu();
      if (!menu) throw new Error("BUG");

      const buttons = _expandItemMenu(isFunction(menu) ? menu(item as T, args) : menu, args);
      const visibleButtons = buttons.filter((x) => isFunction(x) || !x.isVisible || x.isVisible(item));

      if (!visibleButtons.length) return;

      return (
        <SelectStatic
          className="toggle"
          align={ContentAlign.BottomRight}
          isDisabled={props.disableButtons}
          icon={IconType.EllipsisHorizontal}
          style={props.fieldStyle}
          title={""}
          noArrow
        >
          {visibleButtons.map((btn, i) => {
            if (isFunction(btn) || btn["wrappedComponent"]) {
              // wrappedComponent
              const Btn = btn as GridViewButtonComponent<T>;
              return <Btn />;
            }

            const itemProps: Omit<SelectItemProps, "children"> = {
              key: i,
              isDisabled: btn.isDisabled ? btn.isDisabled(item) : false,
              onClick: () =>
                btn.onClick(
                  args.data.find((x) => !isGroupItem(x) && x[args.idKey as string] === item[args.idKey])! as T,
                  args
                ),
              tooltip: btn.tooltipMessage ? btn.tooltipMessage(item) : undefined
            };
            return btn.icon ? (
              <IconSelectItem {...itemProps} caption={btn.label} icon={btn.icon} />
            ) : (
              <SelectItem {...props}>{btn.label}</SelectItem>
            );
          })}
        </SelectStatic>
      );
    },
    [props.disableButtons, props.fieldStyle, data, _expandItemMenu, _getMenu]
  );

  const _getGroupMenu = React.useCallback(() => {
    return grouping?.itemMenu && grouping.itemMenu.length ? grouping.itemMenu : null;
  }, [grouping?.itemMenu]);

  const _renderGroupMenu = React.useCallback(
    (args: GridContentProps<T, T>, item: GridGroupInfo) => {
      const menuMixed = _getGroupMenu();
      if (!menuMixed) throw new Error("BUG");

      const menu = isFunction(menuMixed) ? menuMixed(item) : menuMixed;
      const visibleButtons = menu.filter((x) => isFunction(x) || !x.isVisible || x.isVisible(item));
      if (!visibleButtons.length) return;

      return (
        <SelectStatic
          className="toggle"
          align={ContentAlign.BottomRight}
          icon={IconType.EllipsisHorizontal}
          isDisabled={props.disableButtons}
          noArrow
          style={props.fieldStyle}
          title=""
        >
          {visibleButtons.map((btn, i) => {
            if (isFunction(btn) || btn["wrappedComponent"]) {
              // wrappedComponent
              const Btn = btn as GridViewButtonComponent<T>;
              return <Btn />;
            }

            const itemProps: Omit<SelectItemProps, "children"> = {
              key: i,
              isDisabled: btn.isDisabled ? btn.isDisabled(item) : false,
              onClick: () =>
                btn.onClick(args.data.find((x) => isGroupItem(x) && x.id === item.id)! as unknown as GridGroupInfo<T>, {
                  onAddClick: identity,
                  onDeleteClick: identity,
                  onEditClick: identity
                })
            };
            return btn.icon ? (
              <IconSelectItem {...itemProps} caption={btn.label} icon={btn.icon} />
            ) : (
              <SelectItem {...props}>{btn.label}</SelectItem>
            );
          })}
        </SelectStatic>
      );
    },
    [props.disableButtons, props.fieldStyle, data, _getGroupMenu]
  );

  const onExport = React.useCallback(
    async (emd: ExportModalData) => {
      const data = await exportOptions!.getData();

      switch (emd.format) {
        case "csv": {
          const csv = toCsv(columns, data);
          downloadBlob(new Blob([csv]), `${exportOptions!.fileName ?? i18n.export}.csv`);
          break;
        }
        case "pdf": {
          const div = document.createElement("div");
          const root = createRoot(div);
          flushSync(() => {
            root.render(<Grid columns={columns} data={data} idKey={props.idKey} isExport isLoading={false} />);
          });
          const html = div.innerHTML;
          await exportPdf(html, `${exportOptions!.fileName ?? i18n.export}.pdf`);
          break;
        }
      }

      setVisibleModal(null);
    },
    [i18n, columns, exportOptions, props.idKey, setVisibleModal]
  );

  const onExportClick = React.useCallback(() => {
    setVisibleModal("export");
  }, [setVisibleModal]);

  const _toolbar = React.useMemo(() => {
    if (!exportOptions) return toolbar;

    const result: GridViewToolbar<T> = {
      ...(toolbar ?? GridViewBase.defaultProps.toolbar!)
    };
    result.rightButtons = [
      ...(result.rightButtons ?? []),
      {
        label: exportOptions.title ?? i18n.export,
        onClick: onExportClick,
        icon: IconType.Download,
        type: "white"
      }
    ];
    return result;
  }, [exportOptions, onExportClick, toolbar]);

  // TODO: typescript issues
  return (
    <>
      <GridViewBase
        {...props}
        className={props.className}
        filterFn={filterFn as any}
        i18n={i18n}
        onRowClick={onRowClick}
        onGroupClick={onGroupClick}
        onItemMove={onRowMove}
        data={filteredData}
        toolbar={_toolbar}
        grouping={grouping}
      >
        {(args: GridContentProps<T, T>) => (
          <>
            <Grid
              {...args}
              activeId={props.activeId}
              columns={columns as any}
              expandMode={props.expandMode}
              fixed={props.fixed}
              getRowClassName={(x) =>
                classNames(props.getRowClassName ? props.getRowClassName(x) : undefined, {
                  clickable: args.getIsItemClickable(x)
                })
              }
              gridGroupMenu={_getGroupMenu() ? (item) => _renderGroupMenu(args, item) : undefined}
              gridRowMenu={_getMenu() ? (item) => _renderRowMenu(args, item) : undefined}
              hasGroupMenu={!!_getGroupMenu()}
              hasMenu={!!_getMenu()}
              hideEmptyColumns={props.hideEmptyColumns}
              isHeadless={props.isHeadless}
              isMenuAvailable={props.isMenuAvailable}
              isRowHighlighted={props.isRowHighlighted}
              onCheckboxClick={args.onCheckboxClick}
              onRowClick={args.onRowClick}
              onRowMove={args.onItemMove}
              paddings={props.paddings}
              parentIdProp={props.parentIdProp}
              preHeader={props.preHeader}
              preHeaderGap={props.preHeaderGap}
              renderGroup={renderGroup}
              selectedItems={args.selectedItems}
              tooltip={tooltip}
            />
            {children}
          </>
        )}
      </GridViewBase>

      <ExportModal
        isVisible={visibleModal === "export"}
        onClose={() => setVisibleModal(null)}
        title={exportOptions?.title}
        submit={onExport}
      />
    </>
  );
};
export default GridView;
